import { useEffect, useState } from "react";
import { ErrorNotice } from "../components/AdminFeedback";
import { useLocale } from "../i18n";
import { eventRequest, operation } from "./api";
import { readinessStatus, readinessTask, readinessTaskHref } from "./readiness";
import { valueLabel } from "./labels";
import type { Context, RecordValue } from "./types";

export function PublicationReadiness({ context, expectedVersion, revision, onCheck, onTask }: {
  context: Context; expectedVersion: number; revision: number;
  onCheck: (version: number | null) => void;
  onTask?: (tab: string, read?: string, write?: string) => void;
}) {
  const { w, lang } = useLocale();
  const [data, setData] = useState<RecordValue | null>(null);
  const [error, setError] = useState<unknown>();
  const [refresh, setRefresh] = useState(0);
  const unitId = context.unit?.unitId;
  useEffect(() => {
    const controller = new AbortController();
    setData(null); setError(undefined); onCheck(null);
    if (!unitId) return () => controller.abort();
    void eventRequest(operation("readiness"), { unitId }, undefined, controller.signal).then(result => {
      if (controller.signal.aborted) return;
      setData(result);
      const status = readinessStatus(result, { ...context.unit!, version: expectedVersion });
      onCheck(status === "ready" ? expectedVersion : null);
    }).catch(failure => { if (!controller.signal.aborted) setError(failure); });
    return () => controller.abort();
  }, [unitId, expectedVersion, revision, refresh, onCheck]);
  const status = data ? readinessStatus(data, context.unit ? { ...context.unit, version: expectedVersion } : null) : null;
  const issues = status === "blocked" && Array.isArray(data?.issues) ? data.issues.map(String) : [];
  return <section className="event-readiness" aria-label={w("متطلبات النشر", "Publication requirements")}>
    <div className="panel-title"><h4>{w("متطلبات النشر", "Publication requirements")}</h4>
      <button type="button" className="text-button" onClick={() => { onCheck(null); setRefresh(n => n + 1); }}>{w("تحديث فحص النشر", "Refresh publication check")}</button></div>
    <ErrorNotice operation="read" error={error} retry={() => setRefresh(n => n + 1)} />
    {!data && !error && <p role="status">{w("جارٍ التحقق من متطلبات النشر…", "Checking publication requirements…")}</p>}
    {status === "ready" && <p className="success-box" role="status">{w("اكتملت متطلبات النشر. راجع التغيير ثم أكّد النشر.", "Publication requirements are complete. Review the change, then confirm publishing.")}</p>}
    {(status === "stale" || status === "unknown") && <p className="error-box" role="alert">{w("تغيّرت بيانات المرحلة أو تعذّر تأكيد جاهزيتها. حمّل القيم الحالية قبل النشر.", "The stage changed or readiness could not be confirmed. Load current values before publishing.")}</p>}
    {!!issues.length && <><p role="status">{w("لا يمكن النشر قبل إكمال المتطلبات التالية:", "Complete these requirements before publishing:")}</p>
      <ul className="readiness-tasks">{issues.map(issue => {
        const task = readinessTask(issue, context);
        const label = valueLabel(issue, "issues", lang);
        const href = task ? readinessTaskHref(task, context) : undefined;
        return <li key={issue} className="readiness-task"><div className="readiness-task-copy"><p>{label === issue ? w("راجع إعدادات المرحلة لاستكمال هذا المتطلب.", "Review the stage settings to complete this requirement.") : label}</p></div>
          {task && onTask && (href
            ? <a className="readiness-task-action" href={href}>{w(...task.action)}</a>
            : <button type="button" className="readiness-task-action" onClick={() => onTask(task.tab, task.read, task.write)}>{w(...task.action)}</button>)}</li>;
      })}</ul></>}
  </section>;
}
